import { io, Socket } from 'socket.io-client';

// Get base URL for Socket.IO (same as API but without /api)
const getSocketURL = (): string => {
  const rawEnvBase = import.meta.env.VITE_API_URL as string | undefined;
  let baseURL = rawEnvBase || 'http://localhost:3004';
  
  // Remove /api if present, Socket.IO connects to root
  baseURL = baseURL.replace(/\/api\/?$/, '');
  
  return baseURL;
};

let socket: Socket | null = null;

/**
 * Initialize Socket.IO connection
 */
export const initSocket = (): Socket | null => {
  // Don't create multiple connections
  if (socket?.connected) {
    return socket;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('No token found, cannot initialize socket');
    return null;
  }

  const socketURL = getSocketURL();

  socket = io(socketURL, {
    auth: {
      token: token
    },
    // IMPORTANT: backend mounts Socket.IO on /api/socket.io (see src/server.ts)
    path: '/api/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    autoConnect: true
  });

  socket.on('connect', () => {
    console.log('Socket.IO connected:', socket?.id);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket.IO disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket.IO connection error:', error);
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('Socket.IO reconnected after', attemptNumber, 'attempts');
  });

  socket.on('reconnect_error', (error) => {
    console.error('Socket.IO reconnection error:', error);
  });

  socket.on('reconnect_failed', () => {
    console.error('Socket.IO reconnection failed');
  });

  return socket;
};

/**
 * Get current socket instance
 */
export const getSocket = (): Socket | null => {
  if (!socket) {
    return initSocket();
  }
  return socket;
};

/**
 * Disconnect socket
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Reconnect socket (useful after token refresh)
 */
export const reconnectSocket = (): Socket | null => {
  disconnectSocket();
  return initSocket();
};

/**
 * Join a conversation room
 */
export const joinConversation = (conversationId: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('join_conversation', conversationId);
  }
};

/**
 * Leave a conversation room
 */
export const leaveConversation = (conversationId: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('leave_conversation', conversationId);
  }
};

/**
 * Send a message via Socket.IO
 */
export const sendMessageViaSocket = (
  conversationId: string,
  content: string,
  type: 'text' | 'file' = 'text',
  fileUrl?: string,
  fileName?: string
): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('send_message', {
      conversationId,
      content,
      type,
      fileUrl,
      fileName
    });
  }
};

/**
 * Start typing indicator
 */
export const startTyping = (conversationId: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('typing_start', conversationId);
  }
};

/**
 * Stop typing indicator
 */
export const stopTyping = (conversationId: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('typing_stop', conversationId);
  }
};

/**
 * Mark message as read
 */
export const markMessageAsRead = (conversationId: string, messageId: string): void => {
  const currentSocket = getSocket();
  if (currentSocket) {
    currentSocket.emit('mark_read', {
      conversationId,
      messageId
    });
  }
};

export default {
  initSocket,
  getSocket,
  disconnectSocket,
  reconnectSocket,
  joinConversation,
  leaveConversation,
  sendMessageViaSocket,
  startTyping,
  stopTyping,
  markMessageAsRead
};


